/**
 * offline-session.ts
 * Almacena sesión local con PIN para permitir login offline en modo POS.
 * Después del primer login online exitoso, se cachea la sesión.
 * Si se pierde conexión, el cajero puede re-autenticarse con cédula + PIN.
 */

const DB_NAME = 'ngi-pos-offline'
const DB_VERSION = 2
const STORE_SESSIONS = 'offline_sessions'
const STORE_REGISTER_OPS = 'pending_register_ops'

export interface OfflineSessionData {
  cedula: string              // key
  pinHash: string             // SHA-256(pin + salt)
  salt: string
  userId: string
  orgId: string
  role: string
  fullName: string
  activeStoreId: string
  stores: { id: string; name: string }[]
  categories: { id: string; name: string; color: string | null }[]
  customers: { id: string; name: string; document_type: string | null; document_number: string | null; email: string | null; phone: string | null; credit_enabled: boolean; credit_limit: number; credit_balance: number; customer_type: string | null }[]
  voidSettings: { lockTimeoutMs: number; alwaysRequireAuth: boolean; requireVoidReason: boolean } | null
  cachedAt: string
  expiresAt: string           // 72h from cache
}

// ─── IndexedDB (shared DB with offline-queue) ────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result
      // Keep existing stores
      if (!db.objectStoreNames.contains('pending_sales')) {
        db.createObjectStore('pending_sales', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'cedula' })
      }
      if (!db.objectStoreNames.contains(STORE_REGISTER_OPS)) {
        db.createObjectStore(STORE_REGISTER_OPS, { keyPath: 'id' })
      }
    }
    request.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
    request.onerror = (e) => reject((e.target as IDBOpenDBRequest).error)
  })
}

// ─── Crypto helpers ──────────────────────────────

async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function generateSalt(): string {
  const arr = new Uint8Array(16)
  crypto.getRandomValues(arr)
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
}

// ─── Public API ──────────────────────────────────

export async function saveOfflineSession(
  cedula: string,
  pin: string,
  data: Omit<OfflineSessionData, 'cedula' | 'pinHash' | 'salt' | 'cachedAt' | 'expiresAt'>
): Promise<void> {
  const salt = generateSalt()
  const pinHash = await sha256(pin + salt)
  const now = new Date()
  const expires = new Date(now.getTime() + 72 * 60 * 60 * 1000) // 72 hours

  const session: OfflineSessionData = {
    cedula,
    pinHash,
    salt,
    ...data,
    cachedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
  }

  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readwrite')
    tx.objectStore(STORE_SESSIONS).put(session)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export async function verifyOfflinePin(cedula: string, pin: string): Promise<OfflineSessionData | null> {
  const db = await openDB()
  const session: OfflineSessionData | undefined = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readonly')
    const req = tx.objectStore(STORE_SESSIONS).get(cedula)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })

  if (!session) return null

  // Check expiry
  if (new Date(session.expiresAt) < new Date()) return null

  // Verify PIN
  const hash = await sha256(pin + session.salt)
  if (hash !== session.pinHash) return null

  return session
}

export async function getOfflineSession(cedula: string): Promise<OfflineSessionData | null> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readonly')
    const req = tx.objectStore(STORE_SESSIONS).get(cedula)
    req.onsuccess = () => resolve(req.result ?? null)
    req.onerror = () => reject(req.error)
  })
}

export async function hasAnyOfflineSession(): Promise<boolean> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readonly')
    const req = tx.objectStore(STORE_SESSIONS).count()
    req.onsuccess = () => resolve(req.result > 0)
    req.onerror = () => reject(req.error)
  })
}

// ─── Offline Register Queue ─────────────────────

export interface OfflineRegisterOp {
  id: string
  type: 'open' | 'close'
  storeId: string
  userId: string
  openingAmount?: number
  closingAmount?: number
  notesOpening?: string
  noteClosing?: string
  registerNumber?: number
  createdAt: string
  status: 'pending' | 'synced'
}

export async function queueRegisterOpen(data: {
  storeId: string
  userId: string
  openingAmount: number
  notesOpening?: string
  registerNumber?: number
}): Promise<OfflineRegisterOp> {
  const db = await openDB()
  const op: OfflineRegisterOp = {
    id: `reg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type: 'open',
    storeId: data.storeId,
    userId: data.userId,
    openingAmount: data.openingAmount,
    notesOpening: data.notesOpening,
    registerNumber: data.registerNumber ?? 1,
    createdAt: new Date().toISOString(),
    status: 'pending',
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_REGISTER_OPS, 'readwrite')
    tx.objectStore(STORE_REGISTER_OPS).add(op)
    tx.oncomplete = () => resolve(op)
    tx.onerror = () => reject(tx.error)
  })
}

export async function getPendingRegisterOps(): Promise<OfflineRegisterOp[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_REGISTER_OPS, 'readonly')
    const req = tx.objectStore(STORE_REGISTER_OPS).getAll()
    req.onsuccess = () => resolve((req.result as OfflineRegisterOp[]).filter(o => o.status === 'pending'))
    req.onerror = () => reject(req.error)
  })
}

export async function removeRegisterOp(id: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_REGISTER_OPS, 'readwrite')
    tx.objectStore(STORE_REGISTER_OPS).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// Local register state (localStorage for quick sync checks)
const LOCAL_REGISTER_KEY = 'ngi-pos-offline-register'

export function setLocalRegisterOpen(storeId: string, openingAmount: number) {
  localStorage.setItem(LOCAL_REGISTER_KEY, JSON.stringify({
    isOpen: true,
    storeId,
    openingAmount,
    openedAt: new Date().toISOString(),
  }))
}

export function getLocalRegisterState(): { isOpen: boolean; storeId: string; openingAmount: number; openedAt: string } | null {
  try {
    const raw = localStorage.getItem(LOCAL_REGISTER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export function clearLocalRegisterState() {
  localStorage.removeItem(LOCAL_REGISTER_KEY)
}
